import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { parseBasketJson, useShoppingBasket } from '../../common/hooks/useShoppingBasket';
import { useVoucher } from '../../common/hooks/useVoucher';
import { continueShoppingMsg, quantityMsg, t } from '../../common/i18n/index.js';
import { useAuth } from '../../public/site/AuthProvider';
import { useSessionData } from '../../public/site/SessionDataProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider';
import { getOnlineModelsWithVoucher } from '../../public/site/onlineModel/onlineModelService';
import { useModelsForBasket } from '../PlaceOrder/hooks/useModelsForBasket';
import { handleAddOrRemoveItemForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import { getAddonsForOfferItem } from '../ShoppingBasket/shoppingBasketFunctions';
import { isMobileM2MProduct } from '../ShoppingBasket/shoppingBasketHelpers';
import { getProductImage } from '../ShoppingBasket/shoppingBasketUiUtils';
import {
  calculateTotalPrices,
  findBasketItemForId,
  getAddonsDisplayData,
  getBasketItemStates,
  getDisclaimerFields,
  getEffectivePrice,
  getOffer,
  getOnetimePaymentText,
  getOnlineModelForBasketItem,
  getPriceFromBasketOfferItem,
  getPriceToDisplay,
  getTotalAmount,
  isItemDiscontinued,
} from '../ShoppingBasket/shoppingBasketUtils';
import { BASKET_ITEM_ADDED_EVENT } from './basketPopupEvents';
import { resolveAriaSummary, resolveI18nSummary } from './headerShoppingBasketUtil';
import type { BasketItemAddedDetail } from './basketPopupEvents';
import type { ShoppingBasketType } from '../../common/types/shoppingBasket';
import type { OnlineModel } from '../../generated/api/onlineModel';

export interface HeaderShoppingBasketProps {
  toShoppingCart: string;
}

interface BasketItemUpdate {
  id: string;
  quantity: number;
  guid: string;
  model?: OnlineModel;
}

export const HeaderShoppingBasket = (props: HeaderShoppingBasketProps) => {
  const navigate = useNavigate();
  const companyInfo = useSessionData().companyInfo;
  const eppSolution = companyInfo?.eppSolution;
  const { toShoppingCart } = props;
  const { isAuthenticated } = useAuth();
  const { shoppingBasket: shoppingBasketAsStr } = useShoppingBasket();
  const shoppingBasket: ShoppingBasketType = parseBasketJson(shoppingBasketAsStr);
  const { priceAlteredModels, fetchModelsByCodes, activeVoucherCode, loadVoucherModels, setActiveVoucher } =
    useOnlineModelsContext();
  const basketVoucherCode = shoppingBasket.vouchers?.[0];

  useEffect(() => {
    if (basketVoucherCode !== activeVoucherCode) {
      setActiveVoucher(basketVoucherCode ?? '');
    }
  }, [basketVoucherCode, activeVoucherCode, setActiveVoucher]);

  const [lastItemChanged, setLastItemChanged] = useState<BasketItemUpdate | undefined>(undefined);
  const lastModifiedRef = useRef<number>(0);
  const [pendingBasketEvent, setPendingBasketEvent] = useState<BasketItemAddedDetail | null>(null);
  const loadedVoucherRef = useRef<string | undefined>(undefined);

  const baseModels = useModelsForBasket(shoppingBasket);

  const onlineModelCodeGuids = [...new Set(shoppingBasket.items?.map(cartItem => cartItem.guid))];

  const voucherOnlineModels = basketVoucherCode
    ? getOnlineModelsWithVoucher(onlineModelCodeGuids, basketVoucherCode, priceAlteredModels.onlineModelsByVoucher)
    : undefined;

  const models = baseModels;

  const voucherHook = useVoucher(
    loadVoucherModels,
    setActiveVoucher,
    fetchModelsByCodes,
    shoppingBasket,
    basketVoucherCode,
    voucherOnlineModels
  );

  // Merge voucher models with regular models: use voucher-priced models where available,
  // fall back to regular models for items that don't have voucher pricing yet
  const onlineModels = (() => {
    if (!voucherHook.selectedVoucherCode) {
      return models;
    }
    const voucherModels = voucherHook.updatedModels || [];
    const voucherModelCodes = new Set(voucherModels.map(m => m.onlineModelCode));
    // Include all voucher models, plus any regular models not in voucher set
    const nonVoucherModels = models.filter(m => !voucherModelCodes.has(m.onlineModelCode));
    return [...voucherModels, ...nonVoucherModels];
  })();

  // Load voucher models when voucher code changes or when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && basketVoucherCode && loadedVoucherRef.current !== basketVoucherCode) {
      loadedVoucherRef.current = basketVoucherCode;
      voucherHook.applyVoucher(basketVoucherCode);
    }
    if (!basketVoucherCode) {
      loadedVoucherRef.current = undefined;
    }
  }, [isAuthenticated, basketVoucherCode, voucherHook]);

  const isLoadingOnlineModels =
    onlineModelCodeGuids.length > 0 && (onlineModels.length === 0 || voucherHook.isLoadingModels);

  const basketItems = shoppingBasket.items?.filter(basketItem => !isItemDiscontinued(basketItem, onlineModels));

  // Listen for basket-item-added events dispatched by addToBasket
  useEffect(() => {
    const handler = (e: Event) => {
      setPendingBasketEvent((e as CustomEvent<BasketItemAddedDetail>).detail);
    };
    window.addEventListener(BASKET_ITEM_ADDED_EVENT, handler);
    return () => window.removeEventListener(BASKET_ITEM_ADDED_EVENT, handler);
  }, []);

  // Process pending basket event after basket data and models are ready
  useEffect(() => {
    if (!pendingBasketEvent || isLoadingOnlineModels) {
      return;
    }

    const basketItem = findBasketItemForId(basketItems, pendingBasketEvent.id);
    if (!basketItem) {
      // Basket data hasn't arrived yet (async handleShoppingBasketUpdate) — wait for next render
      return;
    }

    const model = getOnlineModelForBasketItem(onlineModels, basketItem);

    lastModifiedRef.current = Date.now();
    setLastItemChanged({
      id: pendingBasketEvent.id,
      quantity: pendingBasketEvent.quantity,
      guid: pendingBasketEvent.guid ?? '',
      model,
    });
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

    // Analytics: pass currentQty + addedQty so the analytics function can compute the delta
    const newQuantity = (basketItem.quantity ?? 0) + pendingBasketEvent.quantity;
    if (model) {
      handleAddOrRemoveItemForPlaceOrder(basketItem, newQuantity, [model]);
    }

    setPendingBasketEvent(null);
  }, [pendingBasketEvent, shoppingBasketAsStr, isLoadingOnlineModels, onlineModels, basketItems]);

  const basketItemStates = getBasketItemStates(basketItems, onlineModels, eppSolution, isAuthenticated);

  const getProducts = (lastItemChangedInBasket: BasketItemUpdate, modelsForProduct: OnlineModel[]) => {
    const basketItem = findBasketItemForId(basketItems, lastItemChangedInBasket.id);
    if (!basketItem) {
      return [];
    }
    const onlineModelForItem = getOnlineModelForBasketItem(modelsForProduct, basketItem);
    const addedQuantity = lastItemChangedInBasket.quantity;
    const basketItemState = basketItemStates.get(basketItem.id) || {};

    // Use basket price for pending contract confirmations, otherwise fetch from model
    const price = basketItemState.showContractPriceWillBeConfirmedMsg
      ? { price: getPriceFromBasketOfferItem(basketItem.offer) }
      : getEffectivePrice(basketItem, onlineModelForItem, onlineModels);
    const shoppingCartPrice = getPriceToDisplay(price, addedQuantity);
    const offer = getOffer(basketItem.offer.guid, onlineModelForItem);

    const oneTimeCharge = price?.price?.oneTimeCharge ?? basketItem.offer.oneTimePriceInCents;
    const isActivationFee = Boolean(oneTimeCharge && price?.price?.monthlyRecurringCharge);
    const showAddons = onlineModelForItem ? !isMobileM2MProduct(onlineModelForItem) : true;
    const addons =
      showAddons && onlineModelForItem
        ? getAddonsDisplayData(getAddonsForOfferItem(basketItem.offer) ?? [], onlineModelForItem, basketItem)
        : [];

    return [
      {
        addons,
        disclaimer: [
          ...getDisclaimerFields(
            getOnetimePaymentText(oneTimeCharge, addedQuantity, isActivationFee),
            price,
            addedQuantity
          ),
        ],
        id: basketItem.id,
        name: basketItem.name,
        quantity: addedQuantity,
        price: shoppingCartPrice,
        image: getProductImage(basketItem.imageUrl, basketItem.name, offer, onlineModelForItem),
      },
    ];
  };

  const totalProductQuantity = getTotalAmount(basketItems);
  return (
    <div>
      <CL.HeaderShoppingCart
        ariaSummary={resolveAriaSummary(lastItemChanged?.quantity || 0, totalProductQuantity)}
        i18nClose={t.VLZR(continueShoppingMsg)}
        i18nQuantity={t.M0W7(quantityMsg)}
        i18nSubmit={t.VVQP('To shopping cart')}
        i18nSummary={resolveI18nSummary(totalProductQuantity)}
        i18nTitle={t.VJK6('Added to shopping cart')}
        i18nButtonLabel={t.BE8Q('Shopping cart')}
        lastChange={lastModifiedRef.current}
        products={lastItemChanged ? getProducts(lastItemChanged, onlineModels) : []}
        totals={calculateTotalPrices(onlineModels, basketItems, basketItemStates)}
        onSubmit={() => navigate(toShoppingCart)}
        totalProductQuantity={totalProductQuantity}
      />
    </div>
  );
};
